import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { animate, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./ReflexPaywall.module.scss"

const PatternLockIcon = svg.featureIcons.PatternLock
const SecurityPlusIcon = svg.featureIcons.SecurityPlus
const DisguiseIcon = svg.featureIcons.Disguise

/**
 * ReflexPaywall — an original, self-contained FEATURE-SPECIFIC paywall for the
 * real "Face Down Lock" feature ("Instantly app lock by flipping your phone").
 *
 * Divergence brief (claude-paywalls/_ledger.md, concept doc claude-paywalls/reflex.md).
 * Prior feature-specific concepts heroed break-in alerts (Caught, CCTV theatre),
 * smart search (Recall, photo grid) and the decoy passcode (Decoy, peel reveal).
 * Face Down Lock has never been the hero, and this is neither dark nor skeuomorphic.
 *
 *   - Layout:      a bright PRODUCT-DEMO STAGE with one tactile phone you flip —
 *                  not a centered hero+card, not a calendar/road/receipt/map/book.
 *   - Interaction: a physical FLIP gesture (drag the phone face-down) that is a
 *                  timed, consequential micro-interaction — an amber "eye" glances
 *                  over, an EXPOSED meter reads full, and the flip drops it to zero
 *                  and slams a lock plate shut. Auto-demos once, then hands you the
 *                  gesture. No prior paywall is a timed, stateful mini-interaction.
 *   - Register:    cool-confident / reflexive-calm, a touch playful (reaction test).
 *   - Art:         light, flat, non-photographic; brand blue = the LOCKED/safe
 *                  state, amber = the glancing eye / exposure. Tiles are pure-CSS
 *                  gradient "memories" so nothing external is fetched.
 *   - Value frame: SPEED — how fast you can make it private. One motion, instant.
 *
 * Honest & compliant: this demonstrates a real feature; no data-loss claim, no
 * fake countdown, no fake scarcity, real savings arithmetic. Yearly is the
 * featured, default plan and carries the biggest price; monthly is small/secondary.
 */

// Pure-CSS "memory" tiles for the open vault — gradients + a simple content hint
// (arc = landscape, disc = portrait, triangle = video). No external imagery.
type Tile = { g: string; kind: "photo" | "portrait" | "video" }
const TILES: Tile[] = [
  { g: "linear-gradient(150deg,#8fb4ff,#2b5ff5)", kind: "photo" },
  { g: "linear-gradient(150deg,#ffd08a,#ff9d3c)", kind: "portrait" },
  { g: "linear-gradient(150deg,#a6e3c9,#2fae7d)", kind: "video" },
  { g: "linear-gradient(150deg,#ffb0c4,#f2678f)", kind: "photo" },
  { g: "linear-gradient(150deg,#c3b6ff,#7b5be0)", kind: "portrait" },
  { g: "linear-gradient(150deg,#9fd6ff,#3b8fe0)", kind: "video" },
  { g: "linear-gradient(150deg,#ffe0a3,#f2b134)", kind: "photo" },
  { g: "linear-gradient(150deg,#b9f0d6,#3fae86)", kind: "photo" },
  { g: "linear-gradient(150deg,#d9c2ff,#8a63e6)", kind: "video" },
]

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

const ReflexPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── The flip mechanic ────────────────────────────────────────────────────
  // angle: 0 = face-up (vault open, exposed), 180 = face-down (locked).
  const [angle, setAngle] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [flips, setFlips] = useState(0) // how many times the user has locked it
  const [lastMs, setLastMs] = useState<number | null>(null)
  const [demoed, setDemoed] = useState(false)

  const stageRef = useRef<HTMLDivElement>(null)
  const animRef = useRef<ReturnType<typeof animate> | null>(null)
  const angleRef = useRef(0)
  const dragStartAngle = useRef(0)
  const dragStartY = useRef(0)
  const gestureT0 = useRef(0)
  const crossedRef = useRef(false)

  const locked = angle > 90

  const setAngleBoth = useCallback((v: number) => {
    angleRef.current = v
    setAngle(v)
  }, [])

  const stopAnim = useCallback(() => {
    animRef.current?.stop()
    animRef.current = null
  }, [])

  const registerCross = useCallback(
    (nextLocked: boolean, wasLocked: boolean) => {
      if (nextLocked && !wasLocked && !crossedRef.current) {
        crossedRef.current = true
        const dt =
          (typeof performance !== "undefined" ? performance.now() : 0) -
          gestureT0.current
        if (gestureT0.current > 0) setLastMs(Math.max(180, Math.round(dt)))
        setFlips((f) => f + 1)
      }
      if (!nextLocked) crossedRef.current = false
    },
    [],
  )

  const animateTo = useCallback(
    (to: number, duration = 0.9) => {
      stopAnim()
      if (reduceMotion) {
        registerCross(to > 90, angleRef.current > 90)
        setAngleBoth(to)
        return
      }
      const from = angleRef.current
      animRef.current = animate(from, to, {
        duration,
        ease: [0.34, 1.32, 0.5, 1], // slight overshoot → mechanical settle
        onUpdate: (v) => {
          const wasLocked = angleRef.current > 90
          registerCross(v > 90, wasLocked)
          setAngleBoth(v)
        },
      })
    },
    [reduceMotion, setAngleBoth, stopAnim, registerCross],
  )

  // Auto-demo once: flip closed (show the payoff), hold, reopen and hand it over.
  useEffect(() => {
    if (reduceMotion) {
      setDemoed(true)
      return
    }
    const t1 = window.setTimeout(() => {
      gestureT0.current = performance.now()
      animateTo(180, 0.85)
    }, 700)
    const t2 = window.setTimeout(() => animateTo(0, 0.8), 2200)
    const t3 = window.setTimeout(() => setDemoed(true), 3050)
    return () => {
      window.clearTimeout(t1)
      window.clearTimeout(t2)
      window.clearTimeout(t3)
      stopAnim()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      stopAnim()
      setDragging(true)
      dragStartAngle.current = angleRef.current
      dragStartY.current = e.clientY
      gestureT0.current = performance.now()
      crossedRef.current = angleRef.current > 90
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [stopAnim],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return
      const el = stageRef.current
      const h = el ? el.getBoundingClientRect().height : 320
      // Drag down to flip face-down. Full flip ≈ 70% of the stage height.
      const dy = e.clientY - dragStartY.current
      const next = clamp(
        dragStartAngle.current + (dy / (h * 0.7)) * 180,
        0,
        180,
      )
      const wasLocked = angleRef.current > 90
      registerCross(next > 90, wasLocked)
      setAngleBoth(next)
    },
    [dragging, setAngleBoth, registerCross],
  )

  const onPointerUp = useCallback(() => {
    if (!dragging) return
    setDragging(false)
    setDemoed(true)
    animateTo(angleRef.current > 90 ? 180 : 0, 0.45)
  }, [dragging, animateTo])

  const toggleFlip = useCallback(() => {
    setDemoed(true)
    gestureT0.current = performance.now()
    crossedRef.current = angleRef.current > 90
    animateTo(angleRef.current > 90 ? 0 : 180, 0.7)
  }, [animateTo])

  // Exposure meter: full (amber) while face-up, drops to zero (blue) once locked.
  const exposure = clamp(1 - angle / 90, 0, 1)

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const flipSeconds = lastMs != null ? (lastMs / 1000).toFixed(2) : null

  return (
    <div className={styles.root}>
      <div className={styles.wash} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.badge}>
          <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
            <path
              d="M4 8a8 8 0 0113.5-3.5M20 16A8 8 0 016.5 19.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M17 3.5v3.2h-3.2M7 20.5v-3.2h3.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Face Down Lock · Premium
        </span>
        <h1 className={styles.title}>
          Lock it before
          <br />
          <span className={styles.titleAccent}>they even look.</span>
        </h1>
        <p className={styles.sub}>
          Flip your phone face&#8209;down and your whole vault locks in one
          motion — every photo and video gone from the screen. No passcode
          fumbling. Just a reflex.
        </p>
      </div>

      {/* ── THE STAGE: flip the phone face-down ─────────────────────────── */}
      <div className={styles.stageWrap}>
        <div
          className={`${styles.stage} ${dragging ? styles.stageActive : ""}`}
          ref={stageRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          role="slider"
          aria-label="Flip the phone face down to lock the vault"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(angle / 1.8)}
        >
          {/* status readout above the device */}
          <div
            className={`${styles.readout} ${locked ? styles.readoutLocked : styles.readoutOpen}`}
            aria-live="polite"
          >
            <span className={styles.readoutDot} />
            {locked ? "Private — vault locked" : "Open — visible on screen"}
          </div>

          {/* exposure meter */}
          <div className={styles.meter} aria-hidden="true">
            <span className={styles.meterLabel}>Exposed</span>
            <span className={styles.meterTrack}>
              <span
                className={styles.meterFill}
                style={{
                  transform: `scaleX(${exposure})`,
                  background: locked ? "var(--color-primary)" : "#ff9d3c",
                }}
              />
            </span>
            <span className={styles.meterLabel}>Private</span>
          </div>

          <div className={styles.deviceScene}>
            {/* the glancing eye — gives the flip stakes; slides in on a loop */}
            <div
              className={`${styles.eye} ${locked ? styles.eyeBlind : ""}`}
              aria-hidden="true"
            >
              <svg viewBox="0 0 40 24" width="40" height="24">
                <path
                  d="M2 12s7-9 18-9 18 9 18 9-7 9-18 9S2 12 2 12z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <circle cx="20" cy="12" r="4.5" fill="currentColor" />
              </svg>
            </div>

            <motion.div
              className={styles.device}
              style={{ transform: `rotateX(${-angle}deg)` }}
            >
              {/* FRONT — the open vault */}
              <div className={styles.faceFront}>
                <div className={styles.vaultBar}>
                  <span className={styles.vaultTitle}>My Vault</span>
                  <span className={styles.vaultCount}>1,204</span>
                </div>
                <div className={styles.grid}>
                  {TILES.map((t, i) => (
                    <span
                      key={i}
                      className={styles.tile}
                      style={{ background: t.g }}
                    >
                      {t.kind === "photo" && (
                        <span className={styles.tilePhoto} />
                      )}
                      {t.kind === "portrait" && (
                        <span className={styles.tilePortrait} />
                      )}
                      {t.kind === "video" && (
                        <span className={styles.tileVideo}>
                          <svg viewBox="0 0 24 24" width="11" height="11">
                            <path d="M8 5v14l11-7z" fill="#fff" />
                          </svg>
                        </span>
                      )}
                    </span>
                  ))}
                </div>
                <span className={styles.notch} aria-hidden="true" />
              </div>

              {/* BACK — the lock plate */}
              <div className={styles.faceBack}>
                <span className={styles.lockPlate}>
                  <span className={styles.lockRing}>
                    <svg viewBox="0 0 24 24" width="26" height="26">
                      <rect
                        x="4.5"
                        y="10.5"
                        width="15"
                        height="10.5"
                        rx="2.2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                      />
                      <path
                        d="M8 10.5V8a4 4 0 018 0v2.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className={styles.lockText}>VAULT LOCKED</span>
                  <span className={styles.lockSub}>
                    Face down · nothing to see
                  </span>
                </span>
              </div>
            </motion.div>

            {/* drag affordance hint */}
            {!locked && demoed && !dragging && (
              <div className={styles.hint}>
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  aria-hidden="true"
                >
                  <path
                    d="M12 5v14M6 13l6 6 6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                flip it face down
              </div>
            )}
          </div>

          {/* footer of stage: the speed stat + a re-try / flip button */}
          <div className={styles.stageFoot}>
            <div className={styles.stat}>
              {flipSeconds ? (
                <>
                  <strong>{flipSeconds}s</strong> to disappear
                </>
              ) : (
                <>One motion to disappear</>
              )}
            </div>
            <button
              type="button"
              className={styles.flipBtn}
              onClick={toggleFlip}
              aria-label={locked ? "Flip back up" : "Flip face down to lock"}
            >
              {locked ? "Flip back up" : "Flip to lock"}
            </button>
          </div>
        </div>

        <p className={styles.stageCaption}>
          Try it. Face&#8209;down means gone — even mid&#8209;scroll, even when
          someone leans in.
        </p>
      </div>

      {/* the rest of the instant-lock kit — small, secondary to the star */}
      <div className={styles.kit}>
        <span className={styles.kitLead}>Also instant with Premium</span>
        <div className={styles.kitRow}>
          <span className={styles.kitChip}>
            <PatternLockIcon />
            Pattern Lock
          </span>
          <span className={styles.kitChip}>
            <SecurityPlusIcon />
            Passcode + Face ID
          </span>
          <span className={styles.kitChip}>
            <DisguiseIcon />
            App Disguise
          </span>
        </div>
      </div>

      {/* ── PRICING: yearly featured ─────────────────────────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyTop}>
            <span className={styles.yearlyName}>
              Yearly
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
          </span>
          <span className={styles.yearlyPerMo}>
            just {yearlyPerMonth}/mo, billed annually
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Make privacy a reflex"
              : "Turn on Face Down Lock"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `7-day free trial, then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default ReflexPaywall
